<template>
  <nav class="flex flex-1 flex-col">
    <ul role="list" class="flex flex-1 flex-col gap-y-4">
      <li v-for="section in navigation" :key="section.name">
        <div
          v-if="shouldShowHeader(section)"
          :class="[
            'text-xs font-medium leading-6 text-surface-500 dark:text-surface-400 uppercase tracking-widest px-2',
            isMobile ? 'mb-2 tracking-wider' : '',
          ]"
        >
          {{ section.name }}
        </div>
        <ul role="list" class="-mx-2 space-y-1">
          <li v-for="item in section.items" :key="item.name" :class="{ '!mt-0': item.isSubItem }">
            <!-- Special handling for search button -->
            <button
              v-if="item.isSearchButton"
              @click="handleSearchClick"
              :class="[
                'text-surface-700 dark:text-surface-200 hover:text-rch-black dark:hover:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800',
                'group flex gap-x-3 rounded-lg p-2.5 text-sm leading-6 font-medium transition-colors duration-150 w-full text-left',
                collapsed && !isMobile ? 'justify-center' : '',
              ]"
              :title="collapsed && !isMobile ? item.name : ''"
              :data-test-id="`sidebar-nav-${item.routeName}`"
            >
              <component
                :is="item.icon"
                :class="[
                  'text-surface-400 dark:text-surface-500 group-hover:text-rch-black dark:group-hover:text-surface-300',
                  'h-5 w-5 shrink-0',
                ]"
                aria-hidden="true"
              />
              <span v-if="!collapsed || isMobile" class="truncate">{{
                item.name
              }}</span>
            </button>

            <!-- Sub-items with tree connector lines -->
            <div
              v-else-if="item.isSubItem"
              :class="[
                'relative',
                isMobile ? 'ml-12' : collapsed ? '' : 'ml-[22px]',
              ]"
            >
              <!-- Vertical line (connects to next sibling, hidden on last item) -->
              <div
                v-if="!collapsed || isMobile"
                :class="[
                  'absolute left-0 top-0 w-px',
                  isCurrent(item.routeName) ? 'bg-surface-400 dark:bg-surface-400' : 'bg-surface-200 dark:bg-surface-700',
                  isLastSubItem(section.items, item) ? 'h-[16px]' : 'h-full',
                ]"
              />
              <!-- Horizontal branch line -->
              <div
                v-if="!collapsed || isMobile"
                :class="[
                  'absolute left-0 top-[16px] h-px w-3',
                  isCurrent(item.routeName) ? 'bg-surface-400 dark:bg-surface-400' : 'bg-surface-200 dark:bg-surface-700',
                ]"
              />
              <router-link
                :to="{
                  name: item.routeName,
                  params: item.params,
                  query: item.query,
                }"
                @click="closeSidebar"
                :class="[
                  isCurrent(item.routeName)
                    ? 'text-rch-black dark:text-white font-medium'
                    : 'text-surface-500 dark:text-surface-400 hover:text-rch-black dark:hover:text-surface-300',
                  'block pl-5 py-1 text-[13px] leading-6 font-light transition-colors duration-150 truncate',
                  collapsed && !isMobile ? 'pl-2.5 text-center' : '',
                ]"
                :title="collapsed && !isMobile ? item.name : ''"
                :data-test-id="`sidebar-nav-${item.routeName}`"
              >
                {{ item.name }}
              </router-link>
            </div>

            <!-- Regular router links for other items -->
            <router-link
              v-else
              :to="{ name: item.routeName }"
              @click="closeSidebar"
              :class="[
                isCurrent(item.routeName)
                  ? 'bg-surface-200 dark:bg-surface-800/30 text-rch-black dark:text-surface-200'
                  : 'text-surface-700 dark:text-surface-200 hover:text-rch-black dark:hover:text-surface-100 hover:bg-surface-50 dark:hover:bg-surface-800',
                'group flex gap-x-3 rounded-lg p-2.5 text-sm leading-6 font-medium transition-colors duration-150',
                collapsed && !isMobile ? 'justify-center' : '',
              ]"
              :title="collapsed && !isMobile ? item.name : ''"
              :data-test-id="`sidebar-nav-${item.routeName}`"
            >
              <component
                :is="item.icon"
                :class="[
                  isCurrent(item.routeName)
                    ? 'text-rch-black dark:text-surface-200'
                    : 'text-surface-400 dark:text-surface-500 group-hover:text-rch-black dark:group-hover:text-surface-200',
                  'h-5 w-5 shrink-0',
                ]"
                aria-hidden="true"
              />
              <span v-if="!collapsed || isMobile" class="truncate">{{
                item.name
              }}</span>
            </router-link>
          </li>
        </ul>
      </li>
    </ul>
  </nav>
</template>

<script setup>
import { useRoute } from 'vue-router'
import { useSearchStore } from '@/stores/searchStore'

const props = defineProps({
  navigation: {
    type: Array,
    required: true,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close-sidebar', 'search-click'])

const route = useRoute()

const isCurrent = (routeName, transactionId = null) => {
  if (routeName === 'merchantTransactionSearchDetail' && transactionId) {
    return (
      route.name === routeName && route.params.transactionId === transactionId
    )
  }

  // Check exact match or if current route is a descendant (e.g. payout detail under Shopify Stores)
  return (
    route.name === routeName ||
    route.matched.some(r => r.name === routeName)
  )
}

const shouldShowHeader = section => {
  if (props.isMobile) {
    return section.showHeader
  }
  return !props.collapsed && section.showHeader
}

const closeSidebar = () => {
  emit('close-sidebar')
}

const isLastSubItem = (items, currentItem) => {
  const idx = items.indexOf(currentItem)
  for (let i = idx + 1; i < items.length; i++) {
    if (items[i].isSubItem) return false
  }
  return true
}

const handleSearchClick = () => {
  useSearchStore().openSearchDialog()
}
</script>
